import {
    addDoc,
    collection,
    doc,
    getDocs,
    onSnapshot,
    query,
    updateDoc,
    where,
    orderBy,
    limit,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import type { ConsultantPhrase } from '../types';

export const PHRASE_VALIDITY_DAYS = 7;
const PHRASE_TTL_MS = PHRASE_VALIDITY_DAYS * 24 * 60 * 60 * 1000;

export function computePhraseExpiresAt(fromIso = new Date().toISOString()): string {
    return new Date(new Date(fromIso).getTime() + PHRASE_TTL_MS).toISOString();
}

export function isPhraseActive(phrase: Pick<ConsultantPhrase, 'expiresAt' | 'isCurrent'>): boolean {
    return phrase.isCurrent && new Date(phrase.expiresAt).getTime() > Date.now();
}

export function subscribeCurrentConsultantPhrase(
    consultantId: string,
    onPhrase: (phrase: ConsultantPhrase | null) => void,
): () => void {
    const q = query(
        collection(db, 'consultantPhrases'),
        where('consultantId', '==', consultantId),
        where('isCurrent', '==', true),
        limit(1),
    );
    return onSnapshot(q, (snap) => {
        const row = snap.docs
            .map((entry) => ({ ...(entry.data() as Omit<ConsultantPhrase, 'id'>), id: entry.id }))
            .find(isPhraseActive);
        onPhrase(row ?? null);
    });
}

export function subscribeConsultantPhraseHistory(
    consultantId: string,
    onPhrases: (phrases: ConsultantPhrase[]) => void,
): () => void {
    const q = query(
        collection(db, 'consultantPhrases'),
        where('consultantId', '==', consultantId),
        orderBy('createdAt', 'desc'),
        limit(30),
    );
    return onSnapshot(q, (snap) => {
        onPhrases(snap.docs.map((entry) => ({
            ...(entry.data() as Omit<ConsultantPhrase, 'id'>),
            id: entry.id,
        })));
    });
}

export async function setConsultantPhraseOfTheDay(consultantId: string, phrase: string): Promise<void> {
    const trimmed = phrase.trim();
    if (!trimmed) {
        throw new Error('Phrase cannot be empty.');
    }

    const now = new Date().toISOString();
    const currentSnap = await getDocs(query(
        collection(db, 'consultantPhrases'),
        where('consultantId', '==', consultantId),
        where('isCurrent', '==', true),
    ));

    await Promise.all(currentSnap.docs.map((entry) => updateDoc(doc(db, 'consultantPhrases', entry.id), {
        isCurrent: false,
    })));

    await addDoc(collection(db, 'consultantPhrases'), {
        consultantId,
        phrase: trimmed,
        createdAt: now,
        expiresAt: computePhraseExpiresAt(now),
        isCurrent: true,
    } satisfies Omit<ConsultantPhrase, 'id'>);
}

export async function updateCurrentConsultantPhrase(phraseId: string, phrase: string): Promise<void> {
    const trimmed = phrase.trim();
    if (!trimmed) {
        throw new Error('Phrase cannot be empty.');
    }
    await updateDoc(doc(db, 'consultantPhrases', phraseId), { phrase: trimmed });
}
